import {AdacVerdict} from '@/amelipro/interfaces';

/**
 * One product category ADAC answers for.
 *
 * The portal names each of them by a slug of its own, and answers each in a
 * dialect of the same structure: what a row is keyed by changes, and so does
 * the unit the totals count. Everything a screen needs to read a category is
 * written down here, so a category added later is one entry and no new branch.
 */
export interface AdacCategory {
    /** The slug the portal's API is asked with. */
    slug: string;
    /** What the category is called on screen. */
    name: string;
    /** What it is called where room is short: the ADAC card's summary. */
    short: string;
    /** What one unit of the count is called: singular, then plural. */
    unit: [string, string];
    /**
     * The word the card's summary puts after its count, singular then plural:
     * what the category counts. A category keyed by dosage says the dosage
     * instead, and falls back on this one only when the portal gave none.
     */
    counted: [string, string];
    /**
     * What the portal keys the rows by, which is what their labels are built
     * from: one fixed key for a category that counts a single thing, the
     * dosage, or the product.
     */
    keyed: 'single' | 'dosage' | 'product';
}

/**
 * The ADAC categories, as observed on the portal in production on 2026-09-11.
 *
 * These four are the endpoints `atypie-conso` actually calls. The service is
 * also announced for the reusable menstrual protections — cups and pants,
 * reimbursed from 2026-10-01, two per rolling twelve months — which the
 * Assurance maladie adds to ADAC in November 2026, under a slug not seen yet:
 * when it shows up, it is one entry here and nothing else — which is the
 * reason this list is a catalogue and not four branches spread over the screens.
 */
export const ADAC_CATEGORIES: readonly AdacCategory[] = [
    {
        slug: 'bandelettes-glycemie',
        name: 'Bandelettes glycémie',
        short: 'Bandelettes',
        unit: ['bandelette', 'bandelettes'],
        counted: ['remboursée', 'remboursées'],
        keyed: 'single',
    },
    {
        slug: 'capteurs-glucose',
        name: 'Capteurs de glucose',
        short: 'Capteurs',
        unit: ['capteur', 'capteurs'],
        counted: ['remboursé', 'remboursés'],
        keyed: 'single',
    },
    {
        slug: 'subutex-generiques',
        name: 'Subutex et génériques',
        short: 'Subutex',
        unit: ['comprimé', 'comprimés'],
        counted: ['comprimé', 'comprimés'],
        keyed: 'dosage',
    },
    {
        slug: 'medi-onereux',
        name: 'Médicaments onéreux',
        short: 'Médicaments onéreux',
        unit: ['délivrance', 'délivrances'],
        counted: ['médic.', 'médic.'],
        keyed: 'product',
    },
];

/**
 * What the service announces but does not serve yet.
 *
 * Named, and never queried: no slug for them has been seen on the portal, and
 * a guessed one would have Connect report a category as failing when it simply
 * does not exist. They are shown as coming so the pharmacist knows the module
 * is not hiding them.
 */
export const ADAC_UPCOMING_CATEGORIES: readonly string[] = ['Protections menstruelles'];

/** The category a slug names, when it is one this Connect knows. */
export const adacCategory = (slug: string): AdacCategory | null =>
    ADAC_CATEGORIES.find(category => category.slug === slug) ?? null;

/** One line of a verdict, ready to be shown. */
export interface AdacRow {
    /** What the row counts — empty for a category that counts a single thing. */
    label: string;
    /** How much, in the category's unit. */
    total: number;
    /** The day of the last reimbursement of that row, dd/mm/yyyy, when the portal gives one. */
    date?: string;
}

/**
 * Read a verdict as the lines to put on screen, the heaviest first.
 *
 * @param category The category the verdict answers for.
 * @param verdict  What the portal returned.
 */
export const adacRows = (category: AdacCategory, verdict: AdacVerdict): AdacRow[] =>
    Object.entries(verdict.liste)
        .map(([key, row]) => ({
            // A single-count category keys its one row by the thing it counts
            // ("bandelettes"), which the unit already says: labelling it would
            // only print the same word twice.
            label: category.keyed === 'single'
                ? ''
                : (category.keyed === 'dosage' ? row.prefix ?? key : row.libelle ?? key),
            total: row.total,
            date: row.dateRemboursement ?? verdict.dateRemboursement,
        }))
        .sort((first, second) => second.total - first.total);

/** Everything the patient was reimbursed over the period, in the category's unit. */
export const adacTotal = (verdict: AdacVerdict): number =>
    Object.values(verdict.liste).reduce((sum, row) => sum + row.total, 0);

/**
 * Whether ADAC found anything at all over the period.
 *
 * This is all Connect concludes on its own: an empty answer means nothing was
 * reimbursed, and anything else is a figure to be read by the pharmacist. What
 * counts as too much is a professional judgement — a quantity, a threshold and
 * a patient — and it is never made here.
 *
 * @param verdict What the portal returned.
 */
export const adacHasFindings = (verdict: AdacVerdict): boolean =>
    Object.keys(verdict.liste).length > 0;

/**
 * Say a count with its unit, in French.
 *
 * @param count    How many.
 * @param category The category whose unit to use.
 */
export const adacCount = (count: number, category: AdacCategory): string =>
    `${count.toLocaleString('fr-FR')} ${category.unit[count > 1 ? 1 : 0]}`;

/** One category's answer, in the few words of the ADAC card's summary. */
export interface AdacSummary {
    /** The count, which the card sets in bold. */
    count: number;
    /** What follows the count: what it counts, or the dosage and how many others there are. */
    detail: string;
}

/**
 * Say one category's answer in a few words.
 *
 * The devices say how many were reimbursed; Subutex, how much of its main
 * dosage — the other dosages are in the detail, and counted after it; the
 * costly drugs, how many of them. Nothing reimbursed is a bare zero.
 *
 * @param category The category the verdict answers for.
 * @param verdict  What the portal returned.
 */
export const adacSummary = (category: AdacCategory, verdict: AdacVerdict): AdacSummary => {
    const rows = adacRows(category, verdict);

    if (!rows.length) {
        return {count: 0, detail: ''};
    }

    const counted = (count: number): string => category.counted[count > 1 ? 1 : 0];

    switch (category.keyed) {
        case 'dosage': {
            const [main, ...others] = rows;
            const dosage = main.label ? `× ${main.label}` : counted(main.total);

            return {count: main.total, detail: others.length ? `${dosage} +${others.length}` : dosage};
        }
        case 'product':
            return {count: rows.length, detail: counted(rows.length)};
        default: {
            const total = adacTotal(verdict);

            return {count: total, detail: counted(total)};
        }
    }
};

/**
 * Whether ADAC flagged the patient's consumption for a category.
 *
 * ADAC's call, relayed as it is: Connect weighs no quantity against any
 * threshold of its own, and an answer that says nothing is not a flag.
 *
 * @param verdict What the portal returned.
 */
export const adacAlert = (verdict: AdacVerdict): boolean => verdict.alerte === true;
